import {
  DatePicker, Form, FormInstance, Input, Select, Switch,
} from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { isNil } from 'lodash';
import moment from 'moment';
import { DATE_WITH_DASH, TABLE_TIME } from '../../../constants/time-formats';
import {
  selectBookingClientData, selectBookingTimezone, setTimezone,
} from '../../../redux/slices/booking';
import AddressFormSection from '../../../components/AddressFormSection';
import { StepSection } from '../../../components/StepPanel';
import { ClientData } from '../../../model';
import { mobileRegxPattern, trimPhoneNumber } from '../../../utils/phone-number';
import { timezoneForPostcode } from '../../../utils/timezone';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';

const RELATIONSHIPS = ['Partner', 'Parent', 'Child', 'Sibling', 'Friend', 'Carer', 'Other'];

const ClientDetailsForm: FC<{form: FormInstance}> = ({ form }) => {
  const dispatch = useAppDispatch();
  const clientData = useAppSelector(selectBookingClientData) as ClientData;
  const timezone = useAppSelector(selectBookingTimezone);
  const [hasDiffPostal, setHasDiffPostal] = useState(false);
  const [hasPostcode, setHasPostcode] = useState(Boolean(timezoneForPostcode(clientData.addressResidential?.postcode)));

  useEffect(() => {
    const { dateOfBirth, ...otherClientData } = clientData;
    if (!isNil(clientData)) {
      form.setFieldsValue({ ...otherClientData });
      if (dateOfBirth) {
        form.setFieldsValue({
          dateOfBirth: moment(dateOfBirth, DATE_WITH_DASH),
        });
      }
    }
  }, [clientData, form]);

  // The residential postcode sets the appointment timezone as it is typed.
  const handleValuesChange = (changed: { addressResidential?: { postcode?: string } }) => {
    const postcode = changed.addressResidential?.postcode;
    if (postcode === undefined) return;
    const fromPostcode = timezoneForPostcode(postcode);
    setHasPostcode(Boolean(fromPostcode));
    if (fromPostcode && fromPostcode !== timezone) {
      dispatch(setTimezone(fromPostcode));
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      name="clientDetails"
      scrollToFirstError
      onValuesChange={handleValuesChange}
    >
      <StepSection>
        <h3>Contact details</h3>
        <div className="grid2">
          <Form.Item
            label="First name"
            required
            name="firstName"
            rules={[{ required: true, message: 'Please enter your first name.' }]}
          >
            <Input autoComplete="given-name" />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="Last name"
            required
            rules={[{ required: true, message: 'Please enter your last name.' }]}
          >
            <Input autoComplete="family-name" />
          </Form.Item>
          <Form.Item
            name="dateOfBirth"
            label="Date of birth"
            required
            rules={[
              {
                type: 'object' as const,
                required: true,
                message: 'Please enter your date of birth.',
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="DD/MM/YYYY"
              format={TABLE_TIME}
            />
          </Form.Item>
          <Form.Item
            name="mobile"
            label="Mobile number"
            required
            rules={[
              { required: true, message: 'Please enter your mobile number.' },
              {
                transform: trimPhoneNumber,
                pattern: mobileRegxPattern,
                message: 'Please enter an Australian mobile number, e.g. 0412 345 678.',
              },
            ]}
          >
            <Input placeholder="04** *** ***" inputMode="tel" autoComplete="tel" />
          </Form.Item>
        </div>
        <Form.Item
          name="email"
          label="Email"
          required
          rules={[
            { required: true, message: 'Please enter your email.' },
            { type: 'email', message: 'Please enter a valid email address, e.g. name@example.com.' },
          ]}
        >
          <Input inputMode="email" autoComplete="email" />
        </Form.Item>
      </StepSection>

      <AddressFormSection
        title="Residential address"
        addressKey="addressResidential"
        isAllFieldsRequired
        postcodeHint={hasPostcode && (
          <>
            {'Sets your appointment timezone to '}
            <b>{timezone}</b>
            .
          </>
        )}
      />
      <Form.Item label="My postal address is different from my residential address" name="diffPostalAddress" valuePropName="checked" initialValue={false}>
        <Switch onChange={setHasDiffPostal} />
      </Form.Item>
      {hasDiffPostal && <AddressFormSection title="Postal address" addressKey="addressPostal" />}

      <StepSection>
        <h3>
          {'Next of kin '}
          <span className="opt">(optional)</span>
        </h3>
        <Form.Item
          label="Name"
          name={['nextOfKin', 'name']}
        >
          <Input />
        </Form.Item>
        <div className="grid2">
          <Form.Item
            name={['nextOfKin', 'mobile']}
            label="Mobile number"
            rules={[
              {
                transform: trimPhoneNumber,
                pattern: mobileRegxPattern,
                message: 'Please enter an Australian mobile number, e.g. 0412 345 678.',
              },
            ]}
          >
            <Input placeholder="04** *** ***" inputMode="tel" />
          </Form.Item>
          <Form.Item
            label="Relationship to you"
            name={['nextOfKin', 'relationshipToYou']}
          >
            <Select
              allowClear
              placeholder="Please select relationship"
              options={RELATIONSHIPS.map((value) => ({ value, label: value }))}
            />
          </Form.Item>
        </div>
      </StepSection>
    </Form>
  );
};

export default ClientDetailsForm;
